import { FC, MouseEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import LanguageIcon from "@mui/icons-material/Language";
import CheckIcon from "@mui/icons-material/Check";
import {
  normalizeLanguage,
  setUserLanguage,
  SUPPORTED_LANGUAGES,
  SupportedLanguageCode,
} from "utils/language";

const LanguageMenu: FC = () => {
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const currentLanguage = normalizeLanguage(i18n.language);

  const handleOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSelect = useCallback(
    (code: SupportedLanguageCode) => () => {
      setUserLanguage(code);
      setAnchorEl(null);
    },
    [],
  );

  return (
    <>
      <IconButton
        aria-label={t("Select language")}
        aria-haspopup="true"
        aria-controls={anchorEl !== null ? "language-menu" : undefined}
        aria-expanded={anchorEl !== null ? "true" : undefined}
        onClick={handleOpen}
      >
        <LanguageIcon />
      </IconButton>
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={anchorEl !== null}
        onClose={handleClose}
      >
        {SUPPORTED_LANGUAGES.map((language) => (
          <MenuItem
            key={language.code}
            selected={language.code === currentLanguage}
            onClick={handleSelect(language.code)}
          >
            {language.nativeLabel}
            {language.code === currentLanguage && (
              <CheckIcon fontSize="small" sx={{ ml: 1 }} />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguageMenu;
